// VariationsDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Shuffle, Check, ThumbsUp } from 'lucide-react';

/**
 * Dialog for selecting from generated variations
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Array} props.variations - List of variation options
 * @param {number|null} props.selectedIndex - Currently selected variation index
 * @param {function} props.onSelectVariation - Callback when variation is selected
 * @param {function} props.onConfirm - Callback when confirm is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const VariationsDialog = ({
  open,
  onOpenChange,
  variations = [],
  selectedIndex,
  onSelectVariation,
  onConfirm,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shuffle className="h-5 w-5 text-amber-500" />
            Select Your Preferred Option
          </DialogTitle>
          <DialogDescription>
            Click on an option to select it. The selected version will replace the current deliverable.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
          {variations.map((variation, idx) => (
            <Card 
              key={idx}
              className={`cursor-pointer transition-all ${
                selectedIndex === idx 
                  ? 'ring-2 ring-emerald-500 bg-emerald-50' 
                  : variation.status === 'failed' 
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:shadow-lg hover:border-amber-300'
              }`}
              onClick={() => variation.status !== 'failed' && onSelectVariation(idx)}
              data-testid={`variation-option-${idx}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Option {idx + 1}</span>
                  {selectedIndex === idx && (
                    <Badge className="bg-emerald-500">
                      <Check className="h-3 w-3 mr-1" />
                      Selected
                    </Badge>
                  )}
                  {variation.status === 'failed' && (
                    <Badge variant="destructive">Failed</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {variation.type === 'image' && variation.base64 ? (
                  <div className="aspect-video bg-slate-100 rounded overflow-hidden">
                    <img 
                      src={`data:image/png;base64,${variation.base64}`}
                      alt={`Option ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : variation.content ? (
                  <div className="bg-slate-50 rounded p-3 max-h-[200px] overflow-y-auto">
                    <p className="text-sm whitespace-pre-wrap">{variation.content.substring(0, 500)}...</p>
                  </div>
                ) : variation.error ? (
                  <div className="bg-red-50 rounded p-3">
                    <p className="text-sm text-red-600">{variation.error}</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onCancel} data-testid="variations-cancel">
            Cancel
          </Button>
          <Button
            disabled={selectedIndex === null}
            className="bg-emerald-500 hover:bg-emerald-600"
            onClick={onConfirm}
            data-testid="variations-confirm"
          >
            <ThumbsUp className="h-4 w-4 mr-2" />
            Use Selected Option
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VariationsDialog;
